import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { trialsAPI } from '../services/api';

function ClinicalTrialDetail() {
  const { nctId } = useParams();
  const [trial, setTrial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTrial = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await trialsAPI.getByNctId(nctId);
        setTrial(response.data);
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      } finally {
        setLoading(false);
      }
    };

    if (nctId) {
      fetchTrial();
    }
  }, [nctId]);

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading">Loading trial details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="error">Error: {error}</div>
        <Link to="/trials" className="btn-primary" style={{ marginTop: '1rem', display: 'inline-block' }}>
          ← Back to Clinical Trials Search
        </Link>
      </div>
    );
  }

  if (!trial || !trial.protocolSection) {
    return (
      <div className="page-container">
        <div className="no-results">Trial not found</div>
        <Link to="/trials" className="btn-primary" style={{ marginTop: '1rem', display: 'inline-block' }}>
          ← Back to Clinical Trials Search
        </Link>
      </div>
    );
  }

  const protocol = trial.protocolSection;
  const identification = protocol.identificationModule || {};
  const status = protocol.statusModule || {};
  const design = protocol.designModule || {};
  const conditions = protocol.conditionsModule || {};
  const sponsor = protocol.sponsorCollaboratorsModule || {};
  const description = protocol.descriptionModule || {};
  const eligibility = protocol.eligibilityModule || {};
  const contacts = protocol.contactsLocationsModule || {};
  const interventions = protocol.armsInterventionsModule?.interventions || [];
  const outcomes = protocol.outcomesModule || {};

  return (
    <div className="page-container">
      <div style={{ marginBottom: '1.5rem' }}>
        <Link to="/trials" className="btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
          <span>←</span> Back to Search
        </Link>
      </div>

      <div className="trial-detail-container">
        {/* Header Section */}
        <div className="trial-header" style={{ 
          background: 'linear-gradient(135deg, #2E86DE 0%, #1769C1 100%)',
          color: 'white',
          padding: '2rem',
          borderRadius: '12px',
          marginBottom: '2rem',
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)'
        }}>
          <h1 style={{ fontSize: '1.8rem', marginBottom: '1rem', color: 'white' }}>
            {identification.briefTitle || 'Clinical Trial'}
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
            <div>
              <strong>NCT ID:</strong> {identification.nctId || 'N/A'}
            </div>
            <div>
              <strong>Status:</strong>{' '}
              <span style={{ 
                background: status.overallStatus === 'RECRUITING' ? '#27ae60' : 
                           status.overallStatus === 'COMPLETED' ? '#3498db' : 
                           status.overallStatus === 'TERMINATED' ? '#e74c3c' : '#7f8c8d',
                padding: '0.25rem 0.75rem',
                borderRadius: '20px',
                fontWeight: 'bold'
              }}>
                {status.overallStatus || 'N/A'}
              </span>
            </div>
          </div>
        </div>

        {/* Source Link */}
        <div className="source-info" style={{
          background: 'var(--lighter-bg)',
          border: '1px solid var(--border-color)',
          borderRadius: '8px',
          padding: '1rem 1.5rem',
          marginBottom: '2rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div>
            <strong style={{ fontSize: '1rem', color: 'var(--text-color)' }}>📊 Data Source:</strong>
            <span style={{ marginLeft: '0.5rem', color: 'var(--text-light)' }}>ClinicalTrials.gov</span>
          </div>
          <a 
            href={`https://clinicaltrials.gov/study/${identification.nctId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
            style={{ textDecoration: 'none' }}
          >
            View on ClinicalTrials.gov →
          </a>
        </div>

        <div className="trial-content" style={{ display: 'grid', gap: '1.5rem' }}>
          {/* Official Title */}
          {identification.officialTitle && (
            <div className="detail-section">
              <h2>Official Title</h2>
              <p>{identification.officialTitle}</p>
            </div>
          )}

          {/* Study Information */}
          <div className="detail-section">
            <h2>Study Information</h2>
            <div style={{ display: 'grid', gap: '0.75rem' }}>
              <p><strong>Study Type:</strong> {design.studyType || 'N/A'}</p>
              <p><strong>Phase:</strong> {design.phases?.join(', ') || 'Not Applicable'}</p>
              {design.enrollmentInfo && (
                <p><strong>Enrollment:</strong> {design.enrollmentInfo.count || 'N/A'} participants ({design.enrollmentInfo.type || 'Estimated'})</p>
              )}
              {status.startDateStruct && (
                <p><strong>Start Date:</strong> {status.startDateStruct.date || 'N/A'}</p>
              )}
              {status.completionDateStruct && (
                <p><strong>Completion Date:</strong> {status.completionDateStruct.date || 'N/A'} ({status.completionDateStruct.type || 'Estimated'})</p>
              )}
              {status.lastUpdatePostDateStruct && (
                <p><strong>Last Updated:</strong> {status.lastUpdatePostDateStruct.date || 'N/A'}</p>
              )}
            </div>
          </div>

          {/* Conditions */}
          {conditions.conditions && conditions.conditions.length > 0 && (
            <div className="detail-section">
              <h2>Conditions</h2>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {conditions.conditions.map((condition, i) => (
                  <span key={i} className="badge" style={{
                    background: 'var(--primary-light)',
                    color: 'white',
                    padding: '0.5rem 1rem',
                    borderRadius: '20px',
                    fontSize: '0.9rem'
                  }}>
                    {condition}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Brief Summary */}
          {description.briefSummary && (
            <div className="detail-section">
              <h2>Brief Summary</h2>
              <p style={{ whiteSpace: 'pre-wrap', lineHeight: '1.8' }}>{description.briefSummary}</p>
            </div>
          )}

          {/* Detailed Description */}
          {description.detailedDescription && (
            <div className="detail-section">
              <h2>Detailed Description</h2>
              <p style={{ whiteSpace: 'pre-wrap', lineHeight: '1.8' }}>{description.detailedDescription}</p>
            </div>
          )}

          {/* Interventions */}
          {interventions.length > 0 && (
            <div className="detail-section">
              <h2>Interventions</h2>
              <div style={{ display: 'grid', gap: '1rem' }}>
                {interventions.map((intervention, i) => (
                  <div key={i} style={{ 
                    background: 'var(--lighter-bg)', 
                    padding: '1rem',
                    borderRadius: '8px',
                    borderLeft: '4px solid var(--primary-color)'
                  }}>
                    <h4 style={{ color: 'var(--primary-color)', marginBottom: '0.5rem' }}>
                      {intervention.type}: {intervention.name}
                    </h4>
                    {intervention.description && (
                      <p style={{ color: 'var(--text-light)', fontSize: '0.95rem' }}>{intervention.description}</p>
                    )}
                    {intervention.otherNames && intervention.otherNames.length > 0 && (
                      <p style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
                        <strong>Other Names:</strong> {intervention.otherNames.join(', ')}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Primary Outcomes */}
          {outcomes.primaryOutcomes && outcomes.primaryOutcomes.length > 0 && (
            <div className="detail-section">
              <h2>Primary Outcomes</h2>
              <ul style={{ paddingLeft: '1.5rem', lineHeight: '1.8' }}>
                {outcomes.primaryOutcomes.map((outcome, i) => (
                  <li key={i}>
                    <strong>{outcome.measure}</strong>
                    {outcome.timeFrame && ` - ${outcome.timeFrame}`}
                    {outcome.description && <p style={{ marginTop: '0.25rem', color: 'var(--text-light)' }}>{outcome.description}</p>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Secondary Outcomes */}
          {outcomes.secondaryOutcomes && outcomes.secondaryOutcomes.length > 0 && (
            <div className="detail-section">
              <h2>Secondary Outcomes</h2>
              <ul style={{ paddingLeft: '1.5rem', lineHeight: '1.8' }}>
                {outcomes.secondaryOutcomes.map((outcome, i) => (
                  <li key={i}>
                    <strong>{outcome.measure}</strong>
                    {outcome.timeFrame && ` - ${outcome.timeFrame}`}
                    {outcome.description && <p style={{ marginTop: '0.25rem', color: 'var(--text-light)' }}>{outcome.description}</p>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Eligibility */}
          {eligibility && (
            <div className="detail-section">
              <h2>Eligibility Criteria</h2>
              <div style={{ display: 'grid', gap: '0.75rem', marginBottom: '1rem' }}>
                {eligibility.sex && <p><strong>Sex:</strong> {eligibility.sex}</p>}
                {eligibility.minimumAge && (
                  <p><strong>Age Range:</strong> {eligibility.minimumAge} - {eligibility.maximumAge || 'No Maximum'}</p>
                )}
                {eligibility.healthyVolunteers !== undefined && (
                  <p><strong>Healthy Volunteers:</strong> {eligibility.healthyVolunteers ? 'Yes' : 'No'}</p>
                )}
              </div>
              {eligibility.eligibilityCriteria && (
                <div style={{ 
                  background: 'var(--lighter-bg)', 
                  padding: '1rem',
                  borderRadius: '8px',
                  whiteSpace: 'pre-wrap',
                  lineHeight: '1.8'
                }}>
                  {eligibility.eligibilityCriteria}
                </div>
              )}
            </div>
          )}

          {/* Sponsor Information */}
          <div className="detail-section">
            <h2>Sponsor & Collaborators</h2>
            <div style={{ display: 'grid', gap: '0.75rem' }}>
              {sponsor.leadSponsor && (
                <p><strong>Lead Sponsor:</strong> {sponsor.leadSponsor.name} ({sponsor.leadSponsor.class})</p>
              )}
              {sponsor.collaborators && sponsor.collaborators.length > 0 && (
                <div>
                  <strong>Collaborators:</strong>
                  <ul style={{ paddingLeft: '1.5rem', marginTop: '0.5rem' }}>
                    {sponsor.collaborators.map((collab, i) => (
                      <li key={i}>{collab.name} ({collab.class})</li>
                    ))}
                  </ul>
                </div>
              )}
              {sponsor.responsibleParty && (
                <p><strong>Responsible Party:</strong> {sponsor.responsibleParty.type}</p>
              )}
            </div>
          </div>

          {/* Contact Information */}
          {contacts.centralContacts && contacts.centralContacts.length > 0 && (
            <div className="detail-section">
              <h2>Contact Information</h2>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                {contacts.centralContacts.map((contact, i) => (
                  <div key={i}>
                    {contact.name && <p><strong>Name:</strong> {contact.name} {contact.role && `(${contact.role})`}</p>}
                    {contact.phone && <p><strong>Phone:</strong> {contact.phone}</p>}
                    {contact.email && <p><strong>Email:</strong> <a href={`mailto:${contact.email}`}>{contact.email}</a></p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Locations */}
          {contacts.locations && contacts.locations.length > 0 && (
            <div className="detail-section">
              <h2>Study Locations</h2>
              <div style={{ display: 'grid', gap: '1rem' }}>
                {contacts.locations.slice(0, 10).map((location, i) => (
                  <div key={i} style={{ 
                    background: 'var(--lighter-bg)', 
                    padding: '1rem',
                    borderRadius: '8px'
                  }}>
                    <p><strong>{location.facility || 'Facility'}</strong></p>
                    {location.city && location.state && (
                      <p>{location.city}, {location.state} {location.zip || ''}</p>
                    )}
                    {location.country && <p>{location.country}</p>}
                    {location.status && (
                      <p style={{ marginTop: '0.5rem' }}>
                        <span style={{ 
                          background: location.status === 'RECRUITING' ? '#27ae60' : '#7f8c8d',
                          color: 'white',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.85rem'
                        }}>
                          {location.status}
                        </span>
                      </p>
                    )}
                  </div>
                ))}
                {contacts.locations.length > 10 && (
                  <p style={{ color: 'var(--text-light)' }}>
                    ... and {contacts.locations.length - 10} more locations
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer with Source Link */}
        <div style={{ 
          marginTop: '3rem', 
          padding: '1.5rem',
          background: 'var(--lighter-bg)',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <p style={{ marginBottom: '1rem', color: 'var(--text-light)' }}>
            For the most up-to-date information, please visit the official ClinicalTrials.gov website
          </p>
          <a 
            href={`https://clinicaltrials.gov/study/${identification.nctId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
            style={{ textDecoration: 'none' }}
          >
            View Full Details on ClinicalTrials.gov →
          </a>
        </div>
      </div>

      <style jsx="true">{`
        .detail-section {
          background: white;
          padding: 1.5rem;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }
        .detail-section h2 {
          color: var(--secondary-color);
          font-size: 1.3rem;
          margin-bottom: 1rem;
          padding-bottom: 0.5rem;
          border-bottom: 2px solid var(--primary-color);
        }
      `}</style>
    </div>
  );
}

export default ClinicalTrialDetail;
